import { useState, useMemo, useEffect, useRef } from "react";

interface UsePaginationProps<T> {
  items: T[];
  initialPageSize?: number;
  maxItems?: number;
  /**
   * Optional key for sessionStorage persistence.
   * When provided, the current page position is saved so that navigating
   * away and back (e.g. viewing a product then pressing Back) restores
   * the user to the same position in the list.
   * Use a stable string like "products-page".
   */
  storageKey?: string;
}

interface UsePaginationReturn<T> {
  currentItems: T[];
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
  totalShown: number;
  totalItems: number;
  isShowingAll: boolean;
}

const SESSION_PREFIX = "pagination_";

function readFromSession(storageKey: string | undefined, fallback: number): number {
  if (!storageKey) return fallback;
  try {
    const saved = sessionStorage.getItem(SESSION_PREFIX + storageKey);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed > 0) return parsed;
    }
  } catch {
    // sessionStorage unavailable — ignore
  }
  return fallback;
}

export function usePagination<T>({
  items,
  initialPageSize = 8,
  maxItems,
  storageKey,
}: UsePaginationProps<T>): UsePaginationReturn<T> {
  // Initialise from sessionStorage on first render only (lazy init avoids re-reads)
  const [displayCount, setDisplayCount] = useState<number>(() => readFromSession(storageKey, initialPageSize));

  // Track whether we have already clamped to items.length after the async load,
  // so we don't fight with the user's own loadMore() clicks.
  const hasClamped = useRef(false);

  // Persist displayCount to sessionStorage on every change
  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(SESSION_PREFIX + storageKey, String(displayCount));
    } catch {
      // ignore — sessionStorage may be unavailable (private mode etc.)
    }
  }, [displayCount, storageKey]);

  // Once items arrive from the async query, clamp displayCount if the restored
  // value exceeds the real item count (e.g. items were removed since last visit).
  // We only do this once per load; after that the user controls the count via loadMore.
  useEffect(() => {
    if (items.length === 0) {
      hasClamped.current = false; // reset when list clears (e.g. filter change)
      return;
    }
    if (!hasClamped.current && displayCount > items.length) {
      setDisplayCount(items.length);
    }
    hasClamped.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]); // intentionally omit displayCount — we only want to run on item load

  const currentItems = useMemo(() => {
    return items.slice(0, displayCount);
  }, [items, displayCount]);

  const hasMore = useMemo(() => {
    const remaining = items.length - displayCount;
    return remaining > 0 && (!maxItems || displayCount < maxItems);
  }, [items.length, displayCount, maxItems]);

  const loadMore = () => {
    setDisplayCount((prev) => Math.min(prev + initialPageSize, maxItems ?? items.length, items.length));
  };

  const reset = () => {
    setDisplayCount(initialPageSize);
    if (storageKey) {
      try {
        sessionStorage.removeItem(SESSION_PREFIX + storageKey);
      } catch {
        // ignore
      }
    }
  };

  const isShowingAll = displayCount >= items.length || !!(maxItems && displayCount >= maxItems);

  return {
    currentItems,
    hasMore,
    loadMore,
    reset,
    totalShown: currentItems.length,
    totalItems: items.length,
    isShowingAll,
  };
}
